import { create } from "zustand";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";

// Freelancer Job Records.
//
// Every request here carries an account id. That is not a convenience — it is
// the isolation boundary: a record belongs to one freelance account, and the
// server refuses any request that does not name one. The store mirrors that, so
// there is no client-side path that could show one account's work under another.

export type ContractType = "Fixed Price" | "Hourly";

export type WorkSampleType = "Image" | "Portfolio URL" | "Live Website" | "Case Study" | "File";

export interface FreelanceAccountSummary {
  id: string;
  name: string;
  accountName: string;
  platform: string;
  bd: string;
  profilePicUrl: string;
  profileTitle: string;
  jobSuccessScore: number | null;
  profileSkillCount: number;
  recordCount: number;
}

export interface JobRecordRow {
  id: string;
  account: string;
  jobTitle: string;
  category: string;
  subcategory: string;
  contractValue: number;
  currency: string;
  contractType: string;
  duration: string;
  rating: number | null;
  deliveredSummary: string;
  deliverableCount: number;
  workSampleCount: number;
  hasProposal: boolean;
  skills: string[];
  technologies: string[];
  hasAi: boolean;
  aiStale: boolean;
  endDate: string | null;
  isArchived: boolean;
  updatedAt: string;
}

export interface WorkSample {
  id?: string;
  type: WorkSampleType;
  title: string;
  url: string;
  caption?: string;
  asset?: string | null;
  mimeType?: string;
  size?: number;
}

/** AI output, kept in its own object so it can never be mistaken for entered data. */
export interface JobRecordAi {
  highlights: string;
  relevanceSignals: string;
  tags: {
    jobType?: string;
    industry?: string;
    skillsDemonstrated?: string[];
    deliverables?: string[];
    experienceSignal?: string;
    clientSatisfaction?: string;
  };
  /** Which stored fields the insight was derived from. */
  derivedFrom: string[];
  generatedAt: string | null;
  model: string;
  isStale: boolean;
}

export interface JobRecord extends JobRecordRow {
  jobDescription: string;
  jobUrl: string;
  contract: {
    value: number;
    currency: string;
    duration: string;
    startDate: string | null;
    endDate: string | null;
    type: ContractType;
    status: string;
  };
  proposal: {
    coverLetter: string;
    content: string;
    url: string;
    attachments: { name: string; url: string; mimeType?: string; size?: number }[];
  };
  delivered: {
    summary: string;
    items: { id?: string; title: string; detail: string }[];
  };
  feedback: {
    rating: number | null;
    review: string;
    reviewDate: string | null;
    clientName: string;
  };
  workSamples: WorkSample[];
  ai: JobRecordAi;
  createdAt: string;
}

export interface RecordStats {
  totalJobs: number;
  /** null means not enough data — the UI says so rather than showing a zero. */
  totalContractValue: number | null;
  currency: string;
  /** Totals per currency — never summed across them. */
  currencyTotals: { currency: string; value: number }[];
  averageRating: number | null;
  ratedJobs: number;
  topCategory: { name: string; count: number } | null;
}

export interface RecordFilters {
  search: string;
  category: string;
  subcategory: string;
  contractType: string;
  rating: string;
  minValue: string;
  maxValue: string;
  from: string;
  to: string;
  skill: string;
  includeArchived: boolean;
}

export const EMPTY_FILTERS: RecordFilters = {
  search: "",
  category: "",
  subcategory: "",
  contractType: "",
  rating: "",
  minValue: "",
  maxValue: "",
  from: "",
  to: "",
  skill: "",
  includeArchived: false,
};

const EMPTY_STATS: RecordStats = {
  totalJobs: 0,
  totalContractValue: null,
  currency: "USD",
  currencyTotals: [],
  averageRating: null,
  ratedJobs: 0,
  topCategory: null,
};

const EMPTY_OPTIONS = {
  categories: [] as string[],
  subcategories: [] as string[],
  contractTypes: [] as string[],
  skills: [] as string[],
};

interface State {
  accounts: FreelanceAccountSummary[];
  accountId: string;
  isLoadingAccounts: boolean;

  records: JobRecordRow[];
  total: number;
  stats: RecordStats;
  options: typeof EMPTY_OPTIONS;
  isLoading: boolean;

  filters: RecordFilters;
  setFilters: (patch: Partial<RecordFilters>) => void;
  resetFilters: () => void;
  selectAccount: (id: string) => void;

  fetchAccounts: () => Promise<void>;
  fetchRecords: () => Promise<void>;
  fetchRecord: (id: string) => Promise<JobRecord | null>;
  saveRecord: (body: any, id?: string) => Promise<JobRecord | null>;
  patchField: (id: string, field: string, value: any) => Promise<boolean>;
  archiveRecord: (id: string) => Promise<boolean>;
  restoreRecord: (id: string) => Promise<boolean>;
  deleteRecord: (id: string) => Promise<boolean>;
  generateAi: (id: string, force?: boolean) => Promise<JobRecordAi | null>;
  uploadSample: (dataUrl: string, fileName: string, fileSize: number) => Promise<WorkSample | null>;
  findEvidence: (opportunity: any, limit?: number) => Promise<any[] | null>;
}

export const useFreelancerJobRecordsStore = create<State>((set, get) => ({
  accounts: [],
  accountId: "",
  isLoadingAccounts: false,

  records: [],
  total: 0,
  stats: EMPTY_STATS,
  options: EMPTY_OPTIONS,
  isLoading: false,

  filters: { ...EMPTY_FILTERS },

  setFilters: (patch) => set({ filters: { ...get().filters, ...patch } }),
  resetFilters: () => set({ filters: { ...EMPTY_FILTERS } }),

  // Switching accounts clears the table first. Showing the previous account's
  // rows under a new account's name, even for one frame, is the exact confusion
  // this module is built to avoid.
  selectAccount: (id) => {
    set({ accountId: id, records: [], total: 0, stats: EMPTY_STATS, options: EMPTY_OPTIONS });
    if (id) get().fetchRecords();
  },

  fetchAccounts: async () => {
    set({ isLoadingAccounts: true });
    try {
      const res = await axiosInstance.get("/freelancer-job-records/accounts");
      const accounts: FreelanceAccountSummary[] = res.data.accounts || [];
      set({ accounts });
      if (!get().accountId && accounts.length) get().selectAccount(accounts[0]!.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not load freelance accounts.");
    } finally {
      set({ isLoadingAccounts: false });
    }
  },

  fetchRecords: async () => {
    const accountId = get().accountId;
    if (!accountId) return;
    set({ isLoading: true });
    try {
      const f = get().filters;
      const params: Record<string, string> = { account: accountId };
      Object.entries(f).forEach(([k, v]) => {
        if (v !== "" && v !== false && v !== undefined && v !== null) params[k] = String(v);
      });
      const res = await axiosInstance.get("/freelancer-job-records", { params });

      // Switching accounts while a request is in flight would otherwise land the
      // previous account's records under the new account's name — the exact
      // mix-up this module exists to prevent. A late response for an account we
      // have already left is dropped.
      if (get().accountId !== accountId) return;

      set({
        records: res.data.records || [],
        total: res.data.total || 0,
        stats: res.data.stats || EMPTY_STATS,
        options: res.data.options || EMPTY_OPTIONS,
      });
    } catch (error: any) {
      if (get().accountId !== accountId) return;
      toast.error(error.response?.data?.message || "Could not load job records.");
    } finally {
      if (get().accountId === accountId) set({ isLoading: false });
    }
  },

  fetchRecord: async (id) => {
    try {
      const res = await axiosInstance.get(`/freelancer-job-records/${id}`);
      return res.data.record;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not load this job record.");
      return null;
    }
  },

  saveRecord: async (body, id) => {
    try {
      const res = id
        ? await axiosInstance.put(`/freelancer-job-records/${id}`, body)
        : await axiosInstance.post("/freelancer-job-records", {
            ...body,
            account: get().accountId,
          });
      toast.success(res.data?.message || "Saved.");
      await Promise.all([get().fetchRecords(), get().fetchAccounts()]);
      return res.data.record;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not save this job record.");
      return null;
    }
  },

  patchField: async (id, field, value) => {
    try {
      await axiosInstance.patch(`/freelancer-job-records/${id}/field`, { field, value });
      await get().fetchRecords();
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not save that change.");
      return false;
    }
  },

  archiveRecord: async (id) => {
    try {
      const res = await axiosInstance.post(`/freelancer-job-records/${id}/archive`);
      toast.success(res.data?.message || "Archived.");
      await Promise.all([get().fetchRecords(), get().fetchAccounts()]);
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not archive this record.");
      return false;
    }
  },

  restoreRecord: async (id) => {
    try {
      const res = await axiosInstance.post(`/freelancer-job-records/${id}/restore`);
      toast.success(res.data?.message || "Restored.");
      await Promise.all([get().fetchRecords(), get().fetchAccounts()]);
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not restore this record.");
      return false;
    }
  },

  deleteRecord: async (id) => {
    try {
      const res = await axiosInstance.delete(`/freelancer-job-records/${id}`);
      toast.success(res.data?.message || "Deleted.");
      await Promise.all([get().fetchRecords(), get().fetchAccounts()]);
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not delete this record.");
      return false;
    }
  },

  generateAi: async (id, force = false) => {
    try {
      const res = await axiosInstance.post(`/freelancer-job-records/${id}/ai`, { force });
      if (res.data?.regenerated) toast.success(res.data?.message || "AI insights updated.");
      await get().fetchRecords();
      return res.data.ai;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not generate AI insights.");
      return null;
    }
  },

  uploadSample: async (dataUrl, fileName, fileSize) => {
    try {
      const res = await axiosInstance.post("/freelancer-job-records/work-samples", {
        data: dataUrl,
        fileName,
        fileSize,
      });
      return res.data.sample;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not upload that image.");
      return null;
    }
  },

  // The knowledge layer, exposed so the module can be demonstrated end to end:
  // describe a new opportunity, see which past work supports it and why.
  findEvidence: async (opportunity, limit = 5) => {
    const accountId = get().accountId;
    if (!accountId) return null;
    try {
      const res = await axiosInstance.post("/freelancer-job-records/evidence", {
        account: accountId,
        opportunity,
        limit,
      });
      return res.data.matches || [];
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not retrieve supporting work.");
      return null;
    }
  },
}));
